/*
  global
  videojs
*/

import '../../../app.es6';
import angular from 'angular';
import ShotDataView from '../../../lib/shot-data-view.es6';

const FILE_TYPE = 'video/mp4';
const ORIGINAL = 'original';
const LOW = 'low';
const ORIGINAL_LABEL = '標準版';
const LOW_LABEL = 'ライト版';
const MAJOR_VERSION = 1;
const FULL_SIZE = '100%';
const AUTO_SIZE = 'auto';

class VideoPlayScreen {
  /* @ngInject */
  constructor (
    $scope, $sessionStorage, $location, $sce, $timeout,
    commonApiFactory, commonFactory, SwingTypeConstants, Const
  ) {
    this.$scope = $scope;
    this.$sessionStorage = $sessionStorage;
    this.$location = $location;
    this.$sce = $sce;
    this.$timeout = $timeout;
    this.commonApiFactory = commonApiFactory;
    this.commonFactory = commonFactory;
    this.SwingTypeConstants = SwingTypeConstants;
    this.Const = Const;

    // ローディングが残っていた場合非表示にする
    this.commonFactory.overlayHidden();
    let presignedUrls = this.$sessionStorage[this.Const.SSAPP_PRESIGNED_URLS];
    // セッションストレージに動画のpresignedUrlがない場合、生徒トップ画面へ戻す
    if (!presignedUrls) {
      this.$location.path('/member');
      return;
    }
    if (presignedUrls && !presignedUrls.original) {
      this.$location.path('/member');
      return;
    }

    this.video = null;
    this.presignedUrl = '';

    this.activeMessage = false;
    this.activePlayButton = true;
    this.isSuccessCopy = false;
    this.timestamp = new Date().getTime();
    this.isFullScreen = false;

    // device check
    this.isIOS = this.checkIOS();
    this.activeIOSLowVersionMessage = false;
    this.isIOSLowVersion = false;
    if (this.isIOS) {
      // iPhone5Sのとき、landscape時にheightが101%以上でないとアドレスバーが隠れないため、101%を指定する
      this.$sessionStorage[this.Const.SSAPP_FULL_SCREEN_IOS] = true;
      this.activeIOSLowVersionMessage = true;
      this.isIOSLowVersion = this.checkIOSLowVersion();
      this.setTouchEvent();
    }

    // video size setting
    this.videoThumbWidth = FULL_SIZE;
    this.videoThumbHeight = AUTO_SIZE;
    // landscape時にスクリーンのアスペクト比が16:9より横幅の比率が大きい場合は、横幅100%に動画を表示
    this.isWidthMax = true;
    this.setVideoSize();
    window.onresize = () => {
      this.setVideoSize();
      this.$scope.$apply();
    };

    // resolution setting
    this.activeResolutionWindow = false;
    this.activeResolution = ORIGINAL;
    this.resolutionLabel = ORIGINAL_LABEL;

    this.$sessionStorage[this.Const.SSAPP_HAS_HEADER] = false;
    this.$sessionStorage[this.Const.SSAPP_MIN_HEIGHT] = 'min-height-zero';
    this.startedDate = new Date(this.$sessionStorage['videoStartedDate']).toISOString();
    this.endedDate = new Date(this.$sessionStorage['videoEndedDate']).toISOString();
    this.currentTime = 0;

    // shot data setting
    this.dataView = new ShotDataView(this.SwingTypeConstants);
    this.getShotData()
      .then((shotDatas) => {
        this.setVideo();
        this.dataView.appendShots(shotDatas, this.startedDate, this.endedDate);
      });

    window.addEventListener('online', () => {
      if (this.$location.path() === '/member/video') {
        videojs(`video-screen-${this.timestamp}`).removeClass('vjs-seeking');
        this.$scope.$apply();
      }
    }, false);

    if (!this.isIOS) {
      document.addEventListener('webkitfullscreenchange', () => { this.handleFullScreenEvent(event); }, false);
      document.addEventListener('mozfullscreenchange', () => { this.handleFullScreenEvent(event); }, false);
      document.addEventListener('fullscreenchange', () => { this.handleFullScreenEvent(event); }, false);
    }

    VideoPlayScreen.$inject = [
      '$scope', '$sessionStorage', '$location', '$sce', '$timeout',
      'commonApiFactory', 'commonFactory', 'SwingTypeConstants', 'Const'
    ];
  }

  handleFullScreenEvent (event) {
    let doc = window.document;
    if (!doc.fullscreenElement && !doc.mozFullScreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
      this.activePlayButton = false;
      this.togglePlay();
      this.isFullScreen = false;
    }
    this.$scope.$apply();
  }

  // 動画のアスペクト比と表示領域のアスペクト比を比較して、動画のサイズを指定する
  setVideoSize () {
    // 16:9 = width:height
    let videoAspect = 16 / 9;
    let windowAspect;
    // iosだと、アドレスバー部分も表示領域に含まれるため、innerHeightとinnerWidthで計算できない
    if (this.isIOS) {
      if (window.innerHeight >= window.innerWidth) {
        // iosでportraitのとき、動画の横幅に対応するのはwindow.parent.screen.widthになる
        windowAspect = window.parent.screen.width / window.parent.screen.height;
      } else {
        // iosでlandscapeのとき、動画の横幅に対応するのはwindow.parent.screen.heightになる
        windowAspect = window.parent.screen.height / window.parent.screen.width;
      }
    } else {
      windowAspect = window.innerWidth / window.innerHeight;
    }
    // 画面のアスペクト比が16:9より小さい場合(16:9に対して縦の比率が大きい)
    if (windowAspect < videoAspect) {
      this.isWidthMax = true;
      this.videoThumbWidth = FULL_SIZE;
      this.videoThumbHeight = AUTO_SIZE;
    // 画面のアスペクト比が16:9より大きいかつ、横幅が800pxより小さい場合(16:9に対して横の比率が大きい)
    } else if (window.innerWidth < 800 && windowAspect > videoAspect) {
      this.isWidthMax = false;
      // iosの場合、アドレスバーで隠れている部分も表示位置に含まれているため、スクリーンの横幅で計算する。
      if (this.isIOS) {
        this.videoThumbWidth = window.parent.screen.width * videoAspect + 'px';
      } else {
        this.videoThumbWidth = window.innerHeight * videoAspect + 'px';
      }
      this.videoThumbHeight = FULL_SIZE;
    // 画面のアスペクト比が16:9の場合
    } else if (windowAspect === videoAspect) {
      this.isWidthMax = true;
      this.videoThumbWidth = FULL_SIZE;
      this.videoThumbHeight = FULL_SIZE;
    // その他の場合
    } else {
      this.isWidthMax = true;
      this.videoThumbWidth = FULL_SIZE;
      this.videoThumbHeight = AUTO_SIZE;
    }
  }

  togglePlay () {
    if (this.activePlayButton) {
      this.fullScreen();
      this.video.play();
      this.activePlayButton = false;
    } else {
      this.video.pause();
      this.activePlayButton = true;
    }
  }

  checkIOS () {
    if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
      return true;
    } else {
      return false;
    }
  }

  // iosのバージョンが9以下かどうか判定
  checkIOSLowVersion () {
    let ua = navigator.userAgent.toLowerCase();
    let version;
    // case of iphone or ipod
    if (ua.match(/iphone/) || ua.match(/ipod touch/)) {
      version = ua.match(/iphone os ([\d]+)_([\d]+)_([\d]+)/);
      if (!version) {
        version = ua.match(/iphone os ([\d]+)_([\d]+)/);
      }
    }
    // case of ipad
    if (ua.match(/ipad/)) {
      version = ua.match(/cpu os ([\d]+)_([\d]+)_([\d]+)/);
      if (!version) {
        version = ua.match(/cpu os ([\d]+)_([\d]+)/);
      }
    }
    if (version && version[MAJOR_VERSION] <= 9) {
      return true;
    }
    return false;
  }

  // iOSにてタップ1回でclickイベントが効かない要素がある。
  // touchstartでclickイベントを発火するようにする。
  setTouchEvent () {
    let messageButtons = document.querySelectorAll('.video-message-btn');
    for (let i = 0; i < messageButtons.length; i++) {
      messageButtons[i].addEventListener('touchstart', (event) => {
        messageButtons[i].click();
      }, false);
    }

    let textbox = document.getElementById('presigned-url-textbox');
    textbox.addEventListener('touchstart', (event) => {
      textbox.click();
    }, false);
  }

  setVideo () {
    let urls = this.$sessionStorage[this.Const.SSAPP_PRESIGNED_URLS];
    // defaultの解像度はoriginalの720p
    let url = urls.original;
    this.presignedUrl = url;
    this.$scope.$apply();
    // 描画タイミングの問題で、videoタグのidを見つけきれない場合があるため、0.5秒待つ
    this.$timeout(() => {
      let that = this;
      // 再生ボタンをダウンロード不可にする
      let el = document.getElementById(`video-play-button-${this.timestamp}`);
      el.addEventListener('contextmenu', this.cancelEvent, false);
      videojs(`video-screen-${this.timestamp}`).ready(function () {
        that.video = this;
        this.controls(true);
        // videojsが表示している再生ボタンをタップした場合に全画面表示にする
        let playButton = document.getElementsByClassName(this.controlBar.playToggle.el_.className);
        playButton[0].addEventListener('click', () => { that.fullScreen(); }, false);
        playButton[0].addEventListener('touchend', () => { that.fullScreen(); }, false);
        // iOS9以下の場合は、シークバーを非表示
        if (that.isIOS && that.isIOSLowVersion) {
          this.controlBar.addClass('visibility-hidden');
        }
        let sources = [{'type': FILE_TYPE, 'src': that.$sce.trustAsResourceUrl(url)}];
        this.src(sources);
        if (urls && urls.thumb) {
          // サムネイル表示
          this.poster(urls.thumb);
        } else {
          this.load();
        }
        this.on('play', () => {
          that.activePlayButton = false;
          // iosではない場合、全画面表示でないときは再生させない
          if (!that.isFullScreen && !that.isIOS) {
            this.pause();
          }
          that.$scope.$apply();
        });
        this.on('pause', () => {
          that.activePlayButton = true;
          that.$scope.$apply();
        });
        // iosだとvideoのcontrollerをタップした際に、clickイベントとtouchイベントの両方が発火する。
        // そうすると、videojsの再生/停止の制御が2回動いて元の状態に戻ってしまう。
        // そのためiosの場合のみ、もう一度再生/停止制御を実施する。
        if (that.isIOS) {
          this.on('click', (event) => {
            let className = event.target.className;
              // controllerの再生/停止ボタンをタップ時のみ再生/停止を行う
            if (className.indexOf('vjs-play-control') === -1) { return; }
            if (className.indexOf('vjs-playing') || className.indexOf('vjs-paused')) {
              that.togglePlay();
            }
          });
        }
        this.on('timeupdate', () => {
          that.dataView.updateShotData(this.currentTime());
          that.$scope.$apply();
        });
        this.on('error', () => {
          that.checkPresignedUrl(url);
        });
        // ネットワークが切断されて、読み込み分も再生しきったらloadingを表示する。
        this.on('progress', () => {
          let time = this.currentTime();
          if (that.currentTime === time) {
            if (!navigator.onLine) {
              // loading表示
              this.addClass('vjs-seeking');
              that.$scope.$apply();
            }
          } else {
            that.currentTime = time;
          }
        });
        this.on('waiting', () => {
          if (!navigator.onLine) {
            // loading表示
            this.addClass('vjs-seeking');
            that.$scope.$apply();
          }
        });
      });
    }, 500);
  }

  checkPresignedUrl (url) {
    var xmlHttp = new XMLHttpRequest();
    xmlHttp.onreadystatechange = () => {
      if (xmlHttp.readyState === 4 && xmlHttp.status === 403) {
        // 期限切れだった場合、生徒top画面に戻り、presignedUrlを取得し直す。
        this.$location.path('/member');
        this.$scope.$apply();
      }
    };
    xmlHttp.open('GET', url, true);
    xmlHttp.send(null);
  }

  getShotData () {
    if (localStorage.getItem(this.Const.LSAPP_ROLE) !== this.Const.LSAPP_ROLE_MEMBER) {
      return this.getMembersShotData();
    } else {
      return this.getMeShotData();
    }
  }

  getMeShotData () {
    let params = {
      beginDateTime: this.startedDate,
      endDateTime: this.endedDate
    };
    return this.commonApiFactory.getMembersMeShots(params);
  }

  getMembersShotData () {
    let currentMemberId = this.$sessionStorage[this.Const.SSAPP_MEMBER_ID];
    let params = {
      memberId: currentMemberId,
      beginDateTime: this.startedDate,
      endDateTime: this.endedDate
    };
    return this.commonApiFactory.getMembersMemberIdShots(params);
  }

  toggleResolutionWindow () {
    if (this.activeResolutionWindow) {
      this.activeResolutionWindow = false;
    } else {
      this.activeResolutionWindow = true;
    }
  }

  changeResolution (resolution) {
    // 再生中の解像度を選択した場合、なにもしない
    if (resolution === this.activeResolution) {
      this.activeResolutionWindow = false;
      return;
    }
    let el = document.getElementById(`video-screen-${this.timestamp}`);
    switch (resolution) {
      case ORIGINAL:
        el.removeEventListener('contextmenu', this.cancelEvent, false);
        this.activeResolution = ORIGINAL;
        this.resolutionLabel = ORIGINAL_LABEL;
        break;
      case LOW:
        el.addEventListener('contextmenu', this.cancelEvent, false);
        this.activeResolution = LOW;
        this.resolutionLabel = LOW_LABEL;
        break;
    }
    let urls = this.$sessionStorage[this.Const.SSAPP_PRESIGNED_URLS];
    let url = urls[this.activeResolution];
    let time = videojs(`video-screen-${this.timestamp}`).currentTime();
    let that = this;
    videojs(`video-screen-${that.timestamp}`).ready(function () {
      let sources = [{'type': FILE_TYPE, 'src': that.$sce.trustAsResourceUrl(url)}];
      this.src(sources);
      if (urls && urls.thumb) {
        // サムネイル表示
        this.poster(urls.thumb);
      } else {
        this.load();
      }
      // 切り替え前の再生時間を指定
      this.on('loadedmetadata', () => {
        this.currentTime(time);
      });
      this.on('timeupdate', () => {
        that.dataView.updateShotData(this.currentTime());
        that.$scope.$apply();
      });
      this.on('error', () => {
        that.checkPresignedUrl(url);
      });
    });
    this.activeResolutionWindow = false;
  }

  cancelEvent (event) {
    event.preventDefault();
  }

  checkActiveResolution (resolution) {
    return {
      'active-resolution': (this.activeResolution === resolution)
    };
  }

  closeMessage () {
    this.activeMessage = false;
  }

  closeIOSLowVersionMessage () {
    this.activeIOSLowVersionMessage = false;
  }

  copyUrl () {
    let el = document.getElementById('hidden-presigned-url');
    let range = document.createRange();
    range.selectNode(el);
    window.getSelection().addRange(range);
    let result = document.execCommand('copy');
    if (result) {
      this.isSuccessCopy = true;
    } else {
      this.isSuccessCopy = false;
    }
    // ダイアログ表示中は動画停止
    this.video.pause();
    this.activeMessage = true;
  }

  fullScreen () {
    if (this.isFullScreen || this.isIOS) { return; }
    let doc = window.document;
    let el = doc.getElementById('video-full-screen');
    let requestFullScreen = el.requestFullscreen || el.mozRequestFullScreen || el.webkitRequestFullScreen || el.msRequestFullscreen;
    if (!doc.fullscreenElement && !doc.mozFullScreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
      requestFullScreen.call(el);
      this.isFullScreen = true;
    }
  }
}

angular.module('myApp').controller('VideoPlayScreen', VideoPlayScreen);



// WEBPACK FOOTER //
// ./app/components/video/play/video-play-screen.es6